import { Component, OnInit, signal, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { OrgUser, Plan } from '../interfaces';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router, RouterLink } from '@angular/router';
import { CustomValidators } from 'angular-custom-validators';
import { PaymentMethod } from '../enums';
import { collection, doc, getDocs, query, setDoc, where } from 'firebase/firestore';
import { dataStoreSignal, db, functions } from 'src/app/app.component';
import { formatNumericInput, sanitizeUrl, valueToDate } from '../globals';
import { httpsCallable } from 'firebase/functions';
import moment from 'moment';
import { MatStepper, MatStep, MatStepLabel, MatStepperIcon } from '@angular/material/stepper';
import { MatButton } from '@angular/material/button';
import { MatCardImage } from '@angular/material/card';
import { MatFormField, MatLabel, MatInput, MatError, MatHint } from '@angular/material/input';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import { MatCheckbox } from '@angular/material/checkbox';
import { debounceTime, startWith } from 'rxjs';
import { PostcodeApiService } from '../services/postcode-api.service';
import { MatIcon } from '@angular/material/icon';
import { UserSettingsService } from 'src/app/services/user-settings.service';

@Component({
  selector: 'app-pay',
  templateUrl: './pay.component.html',
  styleUrls: ['./pay.component.scss'],
  imports: [
    CommonModule,
    MatStepper,
    MatStep,
    MatStepLabel,
    MatButton,
    RouterLink,
    MatCardImage,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    NgxIntlTelInputModule,
    MatError,
    MatHint,
    MatCheckbox,
    MatStepperIcon,
    MatIcon,
  ],
})
export class PayComponent implements OnInit {
  db = db;
  ds = dataStoreSignal;
  plans = signal<Plan[]>([]);
  selectedPlan = signal<Plan | null>(null);
  paymentMethod = PaymentMethod;
  paymentMethods = computed(() => {
    const methods: string[] = [];
    if (this.ds().publicSettings?.sepaEnabled) {
      methods.push(PaymentMethod.SEPA);
    }
    if (this.ds().publicSettings?.mollieEnabled) {
      methods.push(PaymentMethod.MOLLIE);
    }
    return methods;
  });
  sanitizeUrl = sanitizeUrl;
  formatNumericInput = formatNumericInput;

  planForm: UntypedFormGroup = new UntypedFormGroup({
    planId: new UntypedFormControl('', [Validators.required]),
  });
  paymentForm: UntypedFormGroup = new UntypedFormGroup({
    selectedPaymentOption: new UntypedFormControl('', Validators.required),
  });
  personalForm: UntypedFormGroup = new UntypedFormGroup({
    name: new UntypedFormControl('', [Validators.required]),
    streetName: new UntypedFormControl('', [Validators.required]),
    houseNumber: new UntypedFormControl('', [
      Validators.required,
      CustomValidators.numberInput(false, false, 0),
      Validators.pattern(/^[0-9]*$/),
    ]),
    postal: new UntypedFormControl('', [CustomValidators.postal, Validators.required]),
    city: new UntypedFormControl('', [Validators.required]),
    houseNumberAddition: new UntypedFormControl(),
    iban: new UntypedFormControl('', [CustomValidators.iban, Validators.required]),
  });

  payForm: UntypedFormGroup = new UntypedFormGroup({
    termsAccepted: new UntypedFormControl(false, [Validators.requiredTrue]),
  });

  awaitingResponse = signal(false);
  loading = signal(true);

  constructor(
    private snackBar: MatSnackBar,
    private router: Router,
    public postcodeApiService: PostcodeApiService,
    private userSettingsService: UserSettingsService,
  ) {
    effect(() => {
      const methods = this.paymentMethods();
      if (methods.length === 1 && !this.paymentForm.controls.selectedPaymentOption.value) {
        this.paymentForm.controls.selectedPaymentOption.setValue(methods[0]);
      }
    });
  }

  async ngOnInit() {
    await this.getAndPrepData();

    this.loading.set(false);
  }

  async getAndPrepData() {
    this.planForm.controls.planId.valueChanges.subscribe((value) => {
      this.selectedPlan.set(this.plans()[this.plans().findIndex((plan) => plan.id === value)]);
    });

    this.paymentForm.controls.selectedPaymentOption.valueChanges
      .pipe(startWith(this.paymentForm.controls.selectedPaymentOption.value))
      .subscribe((value) => {
        value === PaymentMethod.SEPA
          ? this.personalForm.controls.iban.enable()
          : this.personalForm.controls.iban.disable();
      });

    const plansSnapshot = await getDocs(
      query(
        collection(db, `organisations/${this.ds().selectedOrganisation.id}/plans`),
        where('visible', '==', true),
      ),
    );

    this.plans.set(
      plansSnapshot.docs
        .map((planDoc) => ({ id: planDoc.id, ...planDoc.data() }) as Plan)
        .sort((a, b) => a.startAmount - b.startAmount),
    );

    if (this.ds().orgUser) {
      this.personalForm.patchValue(this.ds().orgUser);
    }

    this.personalForm.controls.postal.valueChanges.pipe(debounceTime(600)).subscribe(async () => {
      await this.getAddress();
    });
    this.personalForm.controls.houseNumber.valueChanges
      .pipe(debounceTime(600))
      .subscribe(async () => {
        await this.getAddress();
      });
  }

  async getAddress() {
    if (
      this.personalForm.getRawValue().postal !== '' &&
      this.personalForm.controls.postal.valid &&
      this.personalForm.getRawValue().houseNumber !== '' &&
      this.personalForm.controls.houseNumber.valid
    ) {
      const addressSearchResult = await this.postcodeApiService.getAddress(
        this.personalForm.getRawValue().postal,
        this.personalForm.getRawValue().houseNumber,
      );
      if (addressSearchResult) {
        this.personalForm.controls.streetName.patchValue(addressSearchResult.street);
        this.personalForm.controls.city.patchValue(addressSearchResult.city);
      } else {
        this.personalForm.controls.streetName.reset(null);
        this.personalForm.controls.city.reset(null);
      }
    } else {
      this.personalForm.controls.streetName.reset(null);
      this.personalForm.controls.city.reset(null);
    }
  }

  async pay() {
    const selectedPaymentOption = this.paymentForm.value.selectedPaymentOption;

    // Always require plan, personal and payment selection to be valid
    if (!this.planForm.valid || !this.personalForm.valid || !this.paymentForm.valid) {
      return;
    }

    // Only enforce payForm (including terms acceptance) when using MOLLIE
    if (selectedPaymentOption === PaymentMethod.MOLLIE) {
      const termsAccepted = this.payForm.get('termsAccepted')?.value;
      if (!this.payForm.valid || !termsAccepted) {
        return;
      }
    }

    // Check if anything actually changed
    if (this.ds().orgUser) {
      const planChanged = this.planForm.value.planId !== this.ds().orgUser.currentPlan?.id;
      const methodChanged =
        this.paymentForm.value.selectedPaymentOption !== this.ds().orgUser.paymentMethod;

      let personalChanged = false;
      for (const [key, newValue] of Object.entries(this.personalForm.value)) {
        const currentValue = this.ds().orgUser[key];
        if (currentValue === undefined && (newValue === '' || newValue === null)) {
          continue;
        }

        if (currentValue !== newValue) {
          if (!currentValue && !newValue) {
            continue;
          }

          // Check for Dates
          const currentDate = valueToDate(currentValue);
          const newDate = valueToDate(newValue);
          if (currentDate || newDate) {
            const currentMoment = moment(currentDate);
            const newMoment = moment(newDate);
            if (currentMoment.isSame(newMoment, 'day')) {
              continue;
            }
          }

          if (typeof currentValue === 'string' && typeof newValue === 'string') {
            const normCurrent = currentValue.replace(/\s/g, '').toUpperCase();
            const normNew = newValue.replace(/\s/g, '').toUpperCase();
            if (normCurrent === normNew) {
              continue;
            }
          }

          personalChanged = true;
          break;
        }
      }

      const needsPayment =
        this.paymentForm.value.selectedPaymentOption === PaymentMethod.MOLLIE &&
        !this.ds().orgUser.mollieCustomerID;

      if (!planChanged && !methodChanged && !personalChanged && !needsPayment) {
        this.router.navigateByUrl('/profile');
        return;
      }
    }

    const personalForm = this.personalForm.value;
    this.awaitingResponse.set(true);

    if (personalForm.iban) {
      personalForm.iban = personalForm.iban.replace(/\s/g, '').toUpperCase();
    }

    if (personalForm.postal) {
      personalForm.postal = personalForm.postal.toUpperCase().replace(/\s/g, '');
    }

    const organisationId = this.ds().selectedOrganisation.id;
    const orgUserRef = doc(db, `organisations/${organisationId}/users/${this.ds().rootUser.id}`);
    try {
      await this.userSettingsService.update([organisationId], {
        ...personalForm,
        currentPlan: this.selectedPlan(),
        paymentMethod: this.paymentForm.value.selectedPaymentOption,
        signInMethod: this.ds().rootUser?.signInMethod ?? null,
        passenger: true,
      } as Partial<OrgUser>);
    } catch (error) {
      this.awaitingResponse.set(false);
      this.snackBar.open(
        'Er is iets misgegaan, probeer het later opnieuw. Neem contact op met de beheerder als het probleem blijft bestaan.',
        'X',
      );
      console.error(error);
      return;
    }

    if (this.paymentForm.value.selectedPaymentOption === PaymentMethod.MOLLIE) {
      const orderRef = doc(
        collection(db, `organisations/${this.ds().selectedOrganisation.id}/orders`),
      );
      const planRef = doc(
        db,
        `organisations/${this.ds().selectedOrganisation.id}/plans/${this.selectedPlan()!.id}`,
      );
      await setDoc(orderRef, {
        planRef: planRef,
        userRef: orgUserRef,
        status: 'open',
      });
      const callable = httpsCallable(functions, 'mollieInitialPaymentRequest');
      const callableResult = (await callable({
        orderId: orderRef.id,
        orgId: this.ds().selectedOrganisation.id,
      })) as any;
      switch (callableResult.data['status']) {
        case 201:
          window.location.href = callableResult.data['message'];
          break;
        default:
          this.awaitingResponse.set(false);
          this.snackBar.open(
            'Er is een onbekende fout opgetreden. Neem contact op met de beheerder.',
            'X',
          );
          break;
      }
    }

    if (this.paymentForm.value.selectedPaymentOption === PaymentMethod.SEPA) {
      this.router.navigateByUrl('/profile');
    }
  }
}
